import { useSelector } from "react-redux";
import { usernameSelector } from "../../../../../store/api/auth/selectors";
import { useChangePasswordMutation } from "../../../../../store/api/admin/adminApiSlice";
import { useEffect, useState } from "react";
import { IPassword } from "../../../../../store/api/admin/types";
import { IKeyValue } from "../../../types";
import { passwordSettingsSelector } from "../../../../../store/api/admin/selectors";
import { changePasswordPayload } from "../../../../helpers";
import { useStoreDispatch } from "../../../../../store/hooks";
import { setErrorMsg, setOpenToast } from "../../../../../store/helpers/helperReducers";
import { logout, setCredentials } from "../../../../../store/api/auth/reducer";
import { IRetrievedTokenData } from "../../../../../store/api/auth/types";
import { useLoginMutation } from "../../../../../store/api/auth/authApiSlice";
import { LOCAL_STORAGE } from "../../../../../globals/globals";
import { useNavigate } from "react-router-dom";
import { errorMsgSelector } from "../../../../../store/helpers/helperSelectors";

export const useChangePasswordData = () => {

    const dispatch = useStoreDispatch();
    const navigate = useNavigate();
    const [changePassword, changePasswordStatus] = useChangePasswordMutation();
    const [login, { isLoading }] = useLoginMutation();
    const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
    const errorMsg = useSelector(errorMsgSelector);

    const username = useSelector(usernameSelector);
    const passwordSettings = useSelector(passwordSettingsSelector);

    const [errMsg, setErrMsg] = useState<IKeyValue>({});
    const [userData, setUserData] = useState<IPassword>({ id: 1, username: username ?? "" });

    const fieldsToValidate = ["password", "newPassword", "confirmPassword"];
    const fieldsToValidateError = Object.fromEntries(
        Object.entries(errorMsg).filter(([key]) => fieldsToValidate.includes(key))
    );
    const errComponentCount = Object.values(fieldsToValidateError as IKeyValue).some((err) => err !== "");
    const errCount = Object.values(errMsg).some((err) => err !== "");
    const isSaveDisabled = !userData?.password || !userData?.newPassword || !userData?.confirmPassword || errComponentCount || errCount || isLoading || changePasswordStatus.isLoading;
    const savingLoader = isLoading || changePasswordStatus.isLoading;

    const password = "password";
    const newPassword = "newPassword"
    const confirmPassword = "confirmPassword";
    const clearInputList = { "password": "", "newPassword": "", "confirmPassword": "" }
    const localUsername = localStorage.getItem(LOCAL_STORAGE.USER);

    useEffect(() => {
        dispatch(setErrorMsg({ ...errorMsg, ...clearInputList }));
        setUserData({ ...userData, username: username !== undefined && username !== "" ? username : localUsername ?? "" });
    }, []);

    const onChangeHandler = (event: any) => {
        event.preventDefault();
        const { name, value } = event.target;
        if (userData) {
            setUserData({ ...userData, [name]: value });
        }
    };

    const onBlurHandler = (event: any) => {
        if (userData?.newPassword && userData?.confirmPassword && userData?.newPassword !== userData?.confirmPassword) {
            setErrMsg({ ...errMsg, confirmPassword: "Passwords do not match" });
            return;
        }
        else {
            setErrMsg({ ...errMsg, confirmPassword: "" });
        }
    };

    const changePasswordAction = async () => {
        try {
            if (!userData) return;
            const result = await changePassword(changePasswordPayload(userData));
            if ("error" in result && result.error) throw result.error;

            if ("data" in result && result.data?.status === 200) {
                dispatch(
                    setOpenToast({
                        openToast: true,
                        toastType: "success",
                        message: `Password changed successfully.`,
                    })
                );
                setTimeout(() => {
                    dispatch(logout());
                    navigate("/login");
                }, 1000);
            }
        } catch (error) {
            let errorMessage = `${(error as any)?.data?.message || (error as any)?.data || "Error occurred: Please try again later."}`;
            dispatch(
                setOpenToast({
                    openToast: true,
                    toastType: "error",
                    message: `${errorMessage}`,
                })
            );
        };
    }

    const onSubmitHandler = async () => {
        try {
            if (!errCount && userData?.username !== "admin") {
                if (userData) {
                    if (!token) {
                        const tokenData: IRetrievedTokenData = await login({ username: userData?.username ?? "", password: userData?.password ?? "", isResetPassword: !token }).unwrap();
                        if (tokenData) {
                            // Use centralized auth reducer so localStorage and VSNETJWT cookie stay aligned.
                            dispatch(setCredentials({ ...tokenData }));
                            await changePasswordAction();
                        }
                    }
                    else {
                        await changePasswordAction();
                    }
                }
            }
        } catch (error) {
            let errorMessage = (error as any)?.status === 401 || (error as any)?.status === 400 ? "Old Password is invalid." : `${(error as any)?.data?.message || (error as any)?.data || "Error occurred: Please try again later."}`;
            dispatch(
                setOpenToast({
                    openToast: true,
                    toastType: "error",
                    message: `${errorMessage}`,
                })
            );
        }
    };

    return {
        userData,
        onChangeHandler,
        onBlurHandler,
        onSubmitHandler,
        errMsg,
        isSaveDisabled,
        passwordSettings,
        password,
        newPassword,
        confirmPassword,
        savingLoader
    };
}

